import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads/')) return `${BACKEND_URL}${url}`;
  return url;
};

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [tips, setTips] = useState([]);
  const [users, setUsers] = useState([]);
  const [wins, setWins] = useState([]);
  const [news, setNews] = useState([]);
  const [settings, setSettings] = useState({ telegram_link: '', subscription_price: '', free_tip_image: '' });
  const [visitorStats, setVisitorStats] = useState(null);
  const [onlineData, setOnlineData] = useState({ online_count: 0, users: [] });
  const [tipViewStats, setTipViewStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddTip, setShowAddTip] = useState(false);
  const [showAddWin, setShowAddWin] = useState(false);
  const [showAddNews, setShowAddNews] = useState(false);
  const [editingTip, setEditingTip] = useState(null);
  const [editingWin, setEditingWin] = useState(null);
  const [editingNews, setEditingNews] = useState(null);
  const [activeTab, setActiveTab] = useState('tips');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [tipViewers, setTipViewers] = useState({});
  const [expandedTipViewers, setExpandedTipViewers] = useState(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '', description: '', image_url: '', link_url: '', parlay_link: '', is_free: false, sport_type: 'soccer', result: 'pending',
  });
  const [winFormData, setWinFormData] = useState({
    match_name: '', amount_won: '', date: '', image_url: '',
  });
  const [newsFormData, setNewsFormData] = useState({
    title: '', url: '', description: '', is_vip_only: true,
  });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkAction, setBulkAction] = useState(false);
  const [deletionRequests, setDeletionRequests] = useState([]);

  useEffect(() => { checkAdminAndFetchData(); }, []);

  const checkAdminAndFetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) { navigate('/admin/login'); return; }
      const userRes = await axios.get(`${API}/auth/me`);
      if (!userRes.data.is_admin) { navigate('/admin/login'); return; }
      await fetchData();
    } catch (error) { navigate('/admin/login'); }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, tipsRes, usersRes, winsRes, settingsRes, visitorRes, onlineRes, tipViewsRes, newsRes, delRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/tips`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/wins`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/admin/visitor-stats`),
        axios.get(`${API}/admin/online-users`),
        axios.get(`${API}/admin/tip-views`),
        axios.get(`${API}/news`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/deletion-requests`).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setTips(tipsRes.data);
      setUsers(usersRes.data);
      setWins(winsRes.data);
      setSettings(settingsRes.data);
      setVisitorStats(visitorRes.data);
      setOnlineData(onlineRes.data);
      setTipViewStats(tipViewsRes.data);
      setNews(newsRes.data);
      setDeletionRequests(delRes.data);
    } catch (error) { console.error('Error fetching data:', error); }
    finally { setLoading(false); }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try { await axios.put(`${API}/settings`, settings); alert('Settings saved!'); }
    catch (error) { alert('Error: ' + (error.response?.data?.detail || 'Unknown')); }
    finally { setSavingSettings(false); }
  };

  const handleLogout = () => { localStorage.removeItem('token'); navigate('/admin/login'); };

  // === TIP HANDLERS ===
  const handleSubmitTip = async (e) => {
    e.preventDefault();
    try {
      if (editingTip) { await axios.put(`${API}/tips/${editingTip.id}`, formData); }
      else { await axios.post(`${API}/tips`, formData); }
      setFormData({ title: '', description: '', image_url: '', link_url: '', parlay_link: '', is_free: false, sport_type: 'soccer', result: 'pending' });
      setShowAddTip(false); setEditingTip(null); await fetchData();
    } catch (error) { alert('Error: ' + (error.response?.data?.detail || 'Unknown')); }
  };
  const handleEditTip = (tip) => {
    setEditingTip(tip);
    setFormData({ title: tip.title, description: tip.description, image_url: tip.image_url || '', link_url: tip.link_url || '', parlay_link: tip.parlay_link || '', is_free: tip.is_free, sport_type: tip.sport_type || 'soccer', result: tip.result || 'pending' });
    setShowAddTip(true);
  };
  const handleDeleteTip = async (tipId) => {
    if (!window.confirm('Delete this tip?')) return;
    try { await axios.delete(`${API}/tips/${tipId}`); await fetchData(); }
    catch (error) { alert('Error deleting tip'); }
  };
  const handleToggleVisibility = async (tipId) => {
    try { await axios.patch(`${API}/tips/${tipId}/toggle-visibility`); await fetchData(); }
    catch (error) { alert('Error toggling visibility'); }
  };
  const fetchTipViewers = async (tipId) => {
    if (expandedTipViewers === tipId) { setExpandedTipViewers(null); return; }
    try {
      const res = await axios.get(`${API}/admin/tip/${tipId}/viewers`);
      setTipViewers(prev => ({ ...prev, [tipId]: res.data }));
      setExpandedTipViewers(tipId);
    } catch (error) { console.error('Error fetching viewers:', error); }
  };

  // === WIN HANDLERS ===
  const handleSubmitWin = async (e) => {
    e.preventDefault();
    try {
      if (editingWin) { await axios.put(`${API}/wins/${editingWin.id}`, winFormData); }
      else { await axios.post(`${API}/wins`, winFormData); }
      setWinFormData({ match_name: '', amount_won: '', date: '', image_url: '' });
      setShowAddWin(false); setEditingWin(null); await fetchData();
    } catch (error) { alert('Error: ' + (error.response?.data?.detail || 'Unknown')); }
  };
  const handleEditWin = (win) => {
    setEditingWin(win);
    setWinFormData({ match_name: win.match_name, amount_won: win.amount_won, date: win.date, image_url: win.image_url || '' });
    setShowAddWin(true);
  };
  const handleDeleteWin = async (winId) => {
    if (!window.confirm('Delete this win?')) return;
    try { await axios.delete(`${API}/wins/${winId}`); await fetchData(); }
    catch (error) { alert('Error deleting win'); }
  };

  // === NEWS HANDLERS ===
  const handleSubmitNews = async (e) => {
    e.preventDefault();
    try {
      if (editingNews) { await axios.put(`${API}/news/${editingNews.id}`, newsFormData); }
      else { await axios.post(`${API}/news`, newsFormData); }
      setNewsFormData({ title: '', url: '', description: '', is_vip_only: true });
      setShowAddNews(false); setEditingNews(null); await fetchData();
    } catch (error) { alert('Error: ' + (error.response?.data?.detail || 'Unknown')); }
  };
  const handleEditNews = (item) => {
    setEditingNews(item);
    setNewsFormData({ title: item.title, url: item.url, description: item.description || '', is_vip_only: item.is_vip_only });
    setShowAddNews(true);
  };
  const handleDeleteNews = async (newsId) => {
    if (!window.confirm('Delete this news item?')) return;
    try { await axios.delete(`${API}/news/${newsId}`); await fetchData(); }
    catch (error) { alert('Error deleting news'); }
  };

  // === USER HANDLERS ===
  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    try { await axios.delete(`${API}/admin/users/${userId}`); await fetchData(); }
    catch (error) { alert(error.response?.data?.detail || 'Error deleting user'); }
  };
  const handleToggleVip = async (userId) => {
    try { await axios.put(`${API}/admin/users/${userId}/vip`); await fetchData(); }
    catch (error) { alert(error.response?.data?.detail || 'Error toggling VIP'); }
  };
  const handleBulkVip = async (action) => {
    if (selectedUsers.length === 0) { alert('Select users first'); return; }
    if (!window.confirm(`${action === 'activate' ? 'Give VIP to' : 'Remove VIP from'} ${selectedUsers.length} users?`)) return;
    try {
      await axios.post(`${API}/admin/bulk-vip`, { user_ids: selectedUsers, action });
      setSelectedUsers([]); setBulkAction(false); await fetchData();
    } catch (error) { alert(error.response?.data?.detail || 'Error'); }
  };
  const toggleSelectUser = (userId) => {
    setSelectedUsers(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };
  const toggleSelectAll = () => {
    const nonAdmins = users.filter(u => !u.is_admin).map(u => u.id);
    setSelectedUsers(prev => prev.length === nonAdmins.length ? [] : nonAdmins);
  };

  // === IMAGE UPLOAD ===
  const handleImageUpload = async (event, target) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('File too large! Max 5MB'); return; }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await axios.post(`${API}/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.data.success) {
        if (target === 'win') setWinFormData(prev => ({ ...prev, image_url: response.data.image_url }));
        else setFormData(prev => ({ ...prev, image_url: response.data.image_url }));
      }
    } catch (error) { alert('Error uploading image'); }
    finally { setUploadingImage(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e27] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const pendingDeletions = deletionRequests.filter(r => r.status === 'pending').length;

  const tabs = [
    { id: 'tips', label: 'Tips', count: tips.length },
    { id: 'wins', label: 'Wins', count: wins.length },
    { id: 'news', label: 'News', count: news.length },
    { id: 'users', label: 'Users', count: users.length },
    { id: 'deletions', label: 'Deletions', count: pendingDeletions },
    { id: 'visitors', label: 'Analytics' },
    { id: 'settings', label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-[#0a0e27]">
      {/* Header */}
      <header className="bg-white/5 backdrop-blur-xl border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
              </svg>
            </div>
            <h1 className="text-lg font-extrabold text-white">Admin Panel</h1>
          </div>
          <button onClick={handleLogout} className="text-red-400 hover:text-red-300 text-sm font-bold" data-testid="admin-logout-button">
            Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          {[
            { label: 'Online Now', value: onlineData.online_count, color: 'text-green-400', dot: true },
            { label: 'Users', value: stats?.total_users || 0, color: 'text-white' },
            { label: 'VIP Members', value: stats?.active_subscribers || 0, color: 'text-emerald-400' },
            { label: 'Tips', value: stats?.total_tips || 0, color: 'text-blue-400' },
            { label: 'Views Today', value: tipViewStats?.today_views || 0, color: 'text-cyan-400' },
            { label: 'Free Tips', value: stats?.free_tips || 0, color: 'text-orange-400' },
            { label: 'News', value: news.length, color: 'text-purple-400' },
          ].map((s) => (
            <div key={s.label} className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="flex items-center gap-1.5">
                {s.dot && <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>}
                <div className={`text-2xl font-extrabold ${s.color}`}>{s.value}</div>
              </div>
              <div className="text-white/40 text-[10px] font-bold uppercase tracking-wider">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap transition-all ${
                activeTab === tab.id ? 'bg-white text-[#0a0e27]' : 'bg-white/5 text-white/50 hover:bg-white/10 border border-white/10'
              }`}
              data-testid={`${tab.id}-tab`}
            >
              {tab.label} {tab.count !== undefined && <span className="ml-1 opacity-60">({tab.count})</span>}
            </button>
          ))}
        </div>

        {/* ========== TIPS TAB ========== */}
        {activeTab === 'tips' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-extrabold text-white">Manage Tips</h2>
              <button onClick={() => { setShowAddTip(!showAddTip); setEditingTip(null); setFormData({ title: '', description: '', image_url: '', link_url: '', parlay_link: '', is_free: false, sport_type: 'soccer', result: 'pending' }); }}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-bold transition-all"
                data-testid="add-tip-button">
                {showAddTip ? 'Cancel' : '+ New Tip'}
              </button>
            </div>

            {showAddTip && (
              <div className="bg-white/5 rounded-xl p-6 mb-6 border border-white/10">
                <h3 className="text-lg font-bold text-white mb-4">{editingTip ? 'Edit Tip' : 'New Tip'}</h3>
                <form onSubmit={handleSubmitTip} className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-white/60 mb-1 uppercase tracking-wider">Title</label>
                    <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:ring-2 focus:ring-emerald-500 focus:border-transparent" required data-testid="tip-title-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-white/60 mb-1 uppercase tracking-wider">Description</label>
                    <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:ring-2 focus:ring-emerald-500 focus:border-transparent" rows="4" required data-testid="tip-description-input" />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-white/60 mb-1 uppercase tracking-wider">Image URL or Upload</label>
                      {formData.image_url && <img src={getImageUrl(formData.image_url)} alt="Preview" className="h-20 mb-2 rounded-lg object-cover" />}
                      <input type="url" value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 text-sm mb-2" placeholder="https://i.imgur.com/..." />
                      <label className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white/60 px-3 py-2 rounded-lg text-xs font-bold cursor-pointer transition-all border border-white/10">
                        {uploadingImage ? 'Uploading...' : 'Upload Image'}
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, 'tip')} disabled={uploadingImage} />
                      </label>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-white/60 mb-1 uppercase tracking-wider">Parlay Link</label>
                      <input type="url" value={formData.parlay_link} onChange={(e) => setFormData({ ...formData, parlay_link: e.target.value })}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 text-sm" placeholder="https://..." />
                      <label className="block text-xs font-bold text-white/60 mb-1 mt-3 uppercase tracking-wider">External Link</label>
                      <input type="url" value={formData.link_url} onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 text-sm" placeholder="https://..." />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <input type="checkbox" checked={formData.is_free} onChange={(e) => setFormData({ ...formData, is_free: e.target.checked })}
                      className="w-5 h-5 rounded border-white/20 bg-white/5 text-emerald-500 focus:ring-emerald-500" />
                    <label className="text-white/70 text-sm font-bold">FREE tip (visible to everyone)</label>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-white/60 mb-1 uppercase tracking-wider">Sport Type</label>
                      <select value={formData.sport_type} onChange={(e) => setFormData({ ...formData, sport_type: e.target.value })}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent" data-testid="tip-sport-select">
                        <option value="soccer" className="bg-[#0a0e27]">Soccer</option>
                        <option value="nba" className="bg-[#0a0e27]">NBA / Basketball</option>
                        <option value="mlb" className="bg-[#0a0e27]">MLB / Baseball</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-white/60 mb-1 uppercase tracking-wider">Result</label>
                      <select value={formData.result} onChange={(e) => setFormData({ ...formData, result: e.target.value })}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent" data-testid="tip-result-select">
                        <option value="pending" className="bg-[#0a0e27]">Pending</option>
                        <option value="win" className="bg-[#0a0e27]">Win</option>
                        <option value="loss" className="bg-[#0a0e27]">Loss</option>
                      </select>
                    </div>
                  </div>
                  <button type="submit" className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-lg font-bold transition-all" data-testid="submit-tip-button">
                    {editingTip ? 'Update Tip' : 'Post Tip (+ Email All Users)'}
                  </button>
                </form>
              </div>
            )}

            {/* Tips List */}
            <div className="space-y-3">
              {tips.map((tip) => (
                <div key={tip.id} className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
                  <div className="p-4 flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-extrabold ${tip.is_free ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                          {tip.is_free ? 'FREE' : 'VIP'}
                        </span>
                        <span className="px-2 py-0.5 rounded text-[10px] font-extrabold bg-white/5 text-white/50 border border-white/10">
                          {(tip.sport_type || 'soccer').toUpperCase()}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-extrabold ${
                          { win: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
                            loss: 'bg-red-500/20 text-red-400 border border-red-500/30' }[tip.result] || 'bg-white/5 text-white/40 border border-white/10'
                        }`}>
                          {(tip.result || 'pending').toUpperCase()}
                        </span>
                        {tip.is_visible === false && <span className="px-2 py-0.5 rounded text-[10px] font-extrabold bg-red-500/20 text-red-400">HIDDEN</span>}
                        <h3 className="text-white font-bold text-sm truncate">{tip.title}</h3>
                      </div>
                      <p className="text-white/40 text-xs truncate">{tip.description}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => fetchTipViewers(tip.id)}
                        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all ${expandedTipViewers === tip.id ? 'bg-cyan-500/20 text-cyan-400' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}>
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>
                        <span>{tipViewers[tip.id]?.total_unique_viewers || '...'}</span>
                      </button>
                      <button onClick={() => handleToggleVisibility(tip.id)} className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center hover:bg-white/10 transition-all" title="Toggle visibility">
                        <svg className={`w-4 h-4 ${tip.is_visible !== false ? 'text-emerald-400' : 'text-red-400'}`} fill="currentColor" viewBox="0 0 20 20"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>
                      </button>
                      <button onClick={() => handleEditTip(tip)} className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center hover:bg-blue-500/30 transition-all">
                        <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" /></svg>
                      </button>
                      <button onClick={() => handleDeleteTip(tip.id)} className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center hover:bg-red-500/30 transition-all" data-testid={`delete-tip-${tip.id}`}>
                        <svg className="w-4 h-4 text-red-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                      </button>
                    </div>
                  </div>
                  {/* Expanded Viewers */}
                  {expandedTipViewers === tip.id && tipViewers[tip.id] && (
                    <div className="border-t border-white/5 bg-white/[0.02] px-4 py-3">
                      <div className="flex items-center gap-2 mb-3">
                        <svg className="w-4 h-4 text-cyan-400" fill="currentColor" viewBox="0 0 20 20"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>
                        <span className="text-cyan-400 text-xs font-bold">{tipViewers[tip.id].total_unique_viewers} viewers ({tipViewers[tip.id].total_views} total views)</span>
                      </div>
                      {tipViewers[tip.id].viewers.length === 0 ? (
                        <p className="text-white/30 text-xs">No one has viewed this tip yet</p>
                      ) : (
                        <div className="space-y-2">
                          {tipViewers[tip.id].viewers.map((v) => (
                            <div key={v.email || v.user_id} className="flex items-center gap-3">
                              {v.profile_photo ? (
                                <img src={getImageUrl(v.profile_photo)} alt="" className="w-7 h-7 rounded-full object-cover" />
                              ) : (
                                <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center">
                                  <svg className="w-3.5 h-3.5 text-white/30" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" /></svg>
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <span className="text-white text-xs font-bold">{v.user_name || v.email}</span>
                                <span className="text-white/20 text-[10px] ml-2">{v.view_count}x</span>
                              </div>
                              <span className="text-white/20 text-[10px]">{v.first_viewed ? new Date(v.first_viewed).toLocaleDateString() : ''}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ========== WINS TAB ========== */}
        {activeTab === 'wins' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-extrabold text-white">Manage Wins</h2>
              <button onClick={() => { setShowAddWin(!showAddWin); setEditingWin(null); setWinFormData({ match_name: '', amount_won: '', date: '', image_url: '' }); }}
                className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-bold transition-all"
                data-testid="add-win-button">
                {showAddWin ? 'Cancel' : '+ New Win'}
              </button>
            </div>

            {showAddWin && (
              <div className="bg-white/5 rounded-xl p-6 mb-6 border border-white/10">
                <h3 className="text-lg font-bold text-white mb-4">{editingWin ? 'Edit Win' : 'New Win'}</h3>
                <form onSubmit={handleSubmitWin} className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-white/60 mb-1 uppercase tracking-wider">Match Name</label>
                    <input type="text" value={winFormData.match_name} onChange={(e) => setWinFormData({ ...winFormData, match_name: e.target.value })}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30" placeholder="Arsenal vs Chelsea" required />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-white/60 mb-1 uppercase tracking-wider">Amount Won</label>
                      <input type="text" value={winFormData.amount_won} onChange={(e) => setWinFormData({ ...winFormData, amount_won: e.target.value })}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30" placeholder="$500" required />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-white/60 mb-1 uppercase tracking-wider">Date</label>
                      <input type="text" value={winFormData.date} onChange={(e) => setWinFormData({ ...winFormData, date: e.target.value })}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30" placeholder="Feb 20, 2026" required />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-white/60 mb-1 uppercase tracking-wider">Screenshot URL or Upload</label>
                    {winFormData.image_url && <img src={getImageUrl(winFormData.image_url)} alt="Preview" className="h-24 mb-2 rounded-lg object-cover" />}
                    <input type="url" value={winFormData.image_url} onChange={(e) => setWinFormData({ ...winFormData, image_url: e.target.value })}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 text-sm mb-2" placeholder="https://i.imgur.com/..." />
                    <label className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white/60 px-3 py-2 rounded-lg text-xs font-bold cursor-pointer transition-all border border-white/10">
                      {uploadingImage ? 'Uploading...' : 'Upload Screenshot'}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, 'win')} disabled={uploadingImage} />
                    </label>
                  </div>
                  <button type="submit" className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-lg font-bold transition-all">
                    {editingWin ? 'Update Win' : 'Add Win'}
                  </button>
                </form>
              </div>
            )}

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {wins.length === 0 ? (
                <div className="col-span-full bg-white/5 rounded-xl p-12 text-center border border-white/10">
                  <p className="text-white/40">No wins posted yet</p>
                </div>
              ) : wins.map((win) => (
                <div key={win.id} className="bg-white/5 rounded-xl overflow-hidden border border-white/10">
                  {win.image_url && <img src={getImageUrl(win.image_url)} alt={win.match_name} className="w-full h-40 object-cover" />}
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-emerald-400 font-extrabold text-sm">{win.amount_won}</span>
                      <span className="text-white/30 text-xs">{win.date}</span>
                    </div>
                    <h3 className="text-white font-bold text-sm mb-3">{win.match_name}</h3>
                    <div className="flex gap-2">
                      <button onClick={() => handleEditWin(win)} className="flex-1 bg-blue-500/20 text-blue-400 py-2 rounded-lg text-xs font-bold hover:bg-blue-500/30 transition-all">Edit</button>
                      <button onClick={() => handleDeleteWin(win.id)} className="flex-1 bg-red-500/20 text-red-400 py-2 rounded-lg text-xs font-bold hover:bg-red-500/30 transition-all">Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ========== NEWS TAB ========== */}
        {activeTab === 'news' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-extrabold text-white">News & VIP Content</h2>
              <button onClick={() => { setShowAddNews(!showAddNews); setEditingNews(null); setNewsFormData({ title: '', url: '', description: '', is_vip_only: true }); }}
                className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-bold transition-all"
                data-testid="add-news-button">
                {showAddNews ? 'Cancel' : '+ New Content'}
              </button>
            </div>

            {showAddNews && (
              <div className="bg-white/5 rounded-xl p-6 mb-6 border border-white/10">
                <h3 className="text-lg font-bold text-white mb-4">{editingNews ? 'Edit Content' : 'New Content Link'}</h3>
                <form onSubmit={handleSubmitNews} className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-white/60 mb-1 uppercase tracking-wider">Title</label>
                    <input type="text" value={newsFormData.title} onChange={(e) => setNewsFormData({ ...newsFormData, title: e.target.value })}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:ring-2 focus:ring-purple-500 focus:border-transparent" placeholder="NBA Injury Report Update" required data-testid="news-title-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-white/60 mb-1 uppercase tracking-wider">URL / Link</label>
                    <input type="url" value={newsFormData.url} onChange={(e) => setNewsFormData({ ...newsFormData, url: e.target.value })}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:ring-2 focus:ring-purple-500 focus:border-transparent" placeholder="https://..." required data-testid="news-url-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-white/60 mb-1 uppercase tracking-wider">Description (optional)</label>
                    <textarea value={newsFormData.description} onChange={(e) => setNewsFormData({ ...newsFormData, description: e.target.value })}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:ring-2 focus:ring-purple-500 focus:border-transparent" rows="2" placeholder="Brief description..." />
                  </div>
                  <div className="flex items-center gap-3">
                    <input type="checkbox" checked={newsFormData.is_vip_only} onChange={(e) => setNewsFormData({ ...newsFormData, is_vip_only: e.target.checked })}
                      className="w-5 h-5 rounded border-white/20 bg-white/5 text-purple-500 focus:ring-purple-500" />
                    <label className="text-white/70 text-sm font-bold">VIP Only (only visible to subscribers)</label>
                  </div>
                  <button type="submit" className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-lg font-bold transition-all" data-testid="submit-news-button">
                    {editingNews ? 'Update Content' : 'Post Content'}
                  </button>
                </form>
              </div>
            )}

            {/* News List */}
            <div className="space-y-3">
              {news.length === 0 ? (
                <div className="bg-white/5 rounded-xl p-12 text-center border border-white/10">
                  <p className="text-white/40">No news/content links yet. Add your first one!</p>
                </div>
              ) : news.map((item) => (
                <div key={item.id} className="bg-white/5 rounded-xl border border-white/10 p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-extrabold ${item.is_vip_only ? 'bg-purple-500/20 text-purple-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                          {item.is_vip_only ? 'VIP ONLY' : 'PUBLIC'}
                        </span>
                        <h3 className="text-white font-bold text-sm truncate">{item.title}</h3>
                      </div>
                      {item.description && <p className="text-white/40 text-xs truncate">{item.description}</p>}
                      <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 text-xs hover:text-blue-300 truncate block mt-1">{item.url}</a>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => handleEditNews(item)} className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center hover:bg-blue-500/30 transition-all">
                        <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" /></svg>
                      </button>
                      <button onClick={() => handleDeleteNews(item.id)} className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center hover:bg-red-500/30 transition-all" data-testid={`delete-news-${item.id}`}>
                        <svg className="w-4 h-4 text-red-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ========== USERS TAB ========== */}
        {activeTab === 'users' && (
          <div>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="text-xl font-extrabold text-white">Users ({users.length})</h2>
              <div className="flex items-center gap-2 flex-wrap">
                {selectedUsers.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-white/60 text-xs font-bold">{selectedUsers.length} selected</span>
                    <button onClick={() => handleBulkVip('activate')}
                      className="px-3 py-1.5 bg-emerald-500/20 text-emerald-400 rounded-lg text-xs font-bold hover:bg-emerald-500/30 transition-all" data-testid="bulk-vip-activate">
                      Give VIP
                    </button>
                    <button onClick={() => handleBulkVip('deactivate')}
                      className="px-3 py-1.5 bg-amber-500/20 text-amber-400 rounded-lg text-xs font-bold hover:bg-amber-500/30 transition-all" data-testid="bulk-vip-deactivate">
                      Remove VIP
                    </button>
                  </div>
                )}
                <button onClick={toggleSelectAll}
                  className="px-3 py-1.5 bg-white/5 text-white/50 rounded-lg text-xs font-bold hover:bg-white/10 transition-all border border-white/10" data-testid="select-all-users">
                  {selectedUsers.length === users.filter(u => !u.is_admin).length ? 'Deselect All' : 'Select All'}
                </button>
                <div className="flex items-center gap-2 bg-green-500/10 border border-green-500/30 px-3 py-1.5 rounded-full">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                  <span className="text-green-400 text-sm font-extrabold">{onlineData.online_count} Online</span>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              {users.map((user) => (
                <div key={user.id} className={`bg-white/5 rounded-xl p-4 border transition-all ${selectedUsers.includes(user.id) ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-white/10'}`} data-testid={`user-card-${user.id}`}>
                  <div className="flex items-center gap-4">
                    {/* Checkbox for non-admins */}
                    {!user.is_admin && (
                      <input type="checkbox" checked={selectedUsers.includes(user.id)} onChange={() => toggleSelectUser(user.id)}
                        className="w-4 h-4 rounded border-white/20 bg-white/5 text-emerald-500 focus:ring-emerald-500 flex-shrink-0" />
                    )}
                    {user.is_admin && <div className="w-4"></div>}
                    {/* Photo */}
                    <div className="flex-shrink-0 relative">
                      {user.profile_photo ? (
                        <img src={getImageUrl(user.profile_photo)} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-white/10" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                          <svg className="w-6 h-6 text-white/30" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" /></svg>
                        </div>
                      )}
                      {user.is_online && (
                        <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-400 rounded-full border-2 border-[#0a0e27]"></span>
                      )}
                    </div>
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white font-bold text-sm">
                          {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email}
                        </span>
                        {user.is_admin && <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-red-500/20 text-red-400 border border-red-500/30">ADMIN</span>}
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold ${user.subscription_status === 'active' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-white/5 text-white/30 border border-white/10'}`}>
                          {user.subscription_status === 'active' ? 'VIP' : 'FREE'}
                        </span>
                        {user.is_online && (
                          <span className="text-green-400 text-[10px] font-extrabold flex items-center gap-1">
                            <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
                            ONLINE {user.online_minutes > 0 ? `(${user.online_minutes} min)` : ''}
                          </span>
                        )}
                      </div>
                      <div className="text-white/40 text-xs">{user.email}</div>
                      <div className="flex flex-wrap gap-3 mt-1">
                        {user.phone && <span className="text-white/30 text-xs">{user.phone}</span>}
                        {user.city && <span className="text-white/30 text-xs">{user.city}</span>}
                        {user.birthday && <span className="text-white/30 text-xs">{user.birthday}</span>}
                        {user.subscription_provider && <span className="text-white/20 text-[10px]">via {user.subscription_provider}</span>}
                      </div>
                    </div>
                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="text-right mr-2 hidden sm:block">
                        <div className="text-white/20 text-xs">{user.created_at ? new Date(user.created_at).toLocaleDateString() : ''}</div>
                        {user.subscription_end_date && (
                          <div className="text-white/15 text-[10px]">Expires: {new Date(user.subscription_end_date).toLocaleDateString()}</div>
                        )}
                      </div>
                      {!user.is_admin && (
                        <>
                          <button onClick={() => handleToggleVip(user.id)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${user.subscription_status === 'active' ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30' : 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'}`}
                            data-testid={`toggle-vip-${user.id}`}
                            title={user.subscription_status === 'active' ? 'Remove VIP' : 'Give VIP'}>
                            {user.subscription_status === 'active' ? 'Remove VIP' : 'Give VIP'}
                          </button>
                          <button onClick={() => handleDeleteUser(user.id, user.email)}
                            className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center hover:bg-red-500/30 transition-all"
                            data-testid={`delete-user-${user.id}`}
                            title="Delete user">
                            <svg className="w-4 h-4 text-red-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ========== DELETIONS TAB ========== */}
        {activeTab === 'deletions' && (
          <div>
            <h2 className="text-xl font-extrabold text-white mb-4">Account Deletion Requests</h2>
            {deletionRequests.length === 0 ? (
              <div className="bg-white/5 rounded-xl p-8 border border-white/10 text-center">
                <p className="text-white/40 text-sm">No deletion requests yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {deletionRequests.map((req) => (
                  <div key={req.id} className={`bg-white/5 rounded-xl p-4 border ${req.status === 'pending' ? 'border-orange-500/30' : req.status === 'processed' ? 'border-emerald-500/20' : 'border-white/10'}`} data-testid={`deletion-request-${req.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="text-white font-bold text-sm">{req.email}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase ${
                            req.status === 'pending' ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' :
                            req.status === 'processed' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                            'bg-red-500/20 text-red-400 border border-red-500/30'
                          }`}>
                            {req.status}
                          </span>
                        </div>
                        {req.reason && <p className="text-white/40 text-xs mb-1">Reason: {req.reason}</p>}
                        <p className="text-white/20 text-[10px]">
                          Requested: {req.requested_at ? new Date(req.requested_at).toLocaleDateString() : 'N/A'}
                          {req.processed_at && ` · Processed: ${new Date(req.processed_at).toLocaleDateString()}`}
                        </p>
                      </div>
                      {req.status === 'pending' && (
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Delete account for ${req.email}? This permanently removes all their data.`)) return;
                              try {
                                await axios.post(`${API}/admin/deletion-requests/${req.id}/process`);
                                setDeletionRequests(prev => prev.map(r => r.id === req.id ? { ...r, status: 'processed', processed_at: new Date().toISOString() } : r));
                                fetchData();
                              } catch (err) { alert(err.response?.data?.detail || 'Error processing request'); }
                            }}
                            className="px-3 py-1.5 bg-red-500/80 hover:bg-red-500 text-white text-xs font-extrabold rounded-lg transition-all"
                            data-testid={`process-deletion-${req.id}`}
                          >
                            Delete Account
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/admin/deletion-requests/${req.id}/reject`);
                                setDeletionRequests(prev => prev.map(r => r.id === req.id ? { ...r, status: 'rejected', processed_at: new Date().toISOString() } : r));
                              } catch (err) { alert(err.response?.data?.detail || 'Error rejecting request'); }
                            }}
                            className="px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/60 text-xs font-bold rounded-lg transition-all border border-white/10"
                            data-testid={`reject-deletion-${req.id}`}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ========== ANALYTICS TAB ========== */}
        {activeTab === 'visitors' && (
          <div>
            <h2 className="text-xl font-extrabold text-white mb-4">Analytics</h2>
            
            {/* Online Users */}
            <div className="bg-white/5 rounded-xl p-5 border border-white/10 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></span>
                <h3 className="text-white font-bold">Online Now: <span className="text-green-400">{onlineData.online_count}</span></h3>
              </div>
              {onlineData.users.length > 0 ? (
                <div className="flex flex-wrap gap-3">
                  {onlineData.users.map((u) => (
                    <div key={u.email || u.user_id} className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2">
                      {u.profile_photo ? (
                        <img src={getImageUrl(u.profile_photo)} alt="" className="w-7 h-7 rounded-full object-cover" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center">
                          <svg className="w-3.5 h-3.5 text-white/30" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" /></svg>
                        </div>
                      )}
                      <span className="text-white text-xs font-bold">{u.first_name ? `${u.first_name} ${u.last_name || ''}`.trim() : u.email.split('@')[0]}</span>
                      <span className={`w-1.5 h-1.5 rounded-full ${u.subscription_status === 'active' ? 'bg-emerald-400' : 'bg-white/20'}`}></span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-white/30 text-sm">No users online right now</p>
              )}
            </div>

            {/* Tip Views */}
            {tipViewStats && (
              <div className="mb-6">
                <h3 className="text-white font-bold mb-3">Tip Views</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: 'Today', value: tipViewStats.today_views, sub: `${tipViewStats.unique_today} unique`, color: 'text-blue-400' },
                    { label: 'This Week', value: tipViewStats.week_views, sub: `${tipViewStats.unique_week} unique`, color: 'text-cyan-400' },
                    { label: 'This Month', value: tipViewStats.month_views, color: 'text-purple-400' },
                    { label: 'All Time', value: tipViewStats.total_views, color: 'text-orange-400' },
                  ].map((s) => (
                    <div key={s.label} className="bg-white/5 rounded-xl p-4 border border-white/10 text-center">
                      <div className={`text-2xl font-extrabold ${s.color}`}>{s.value}</div>
                      <div className="text-white/40 text-[10px] font-bold uppercase tracking-wider">{s.label}</div>
                      {s.sub && <div className="text-white/20 text-[10px] mt-0.5">{s.sub}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Page Visitors */}
            {visitorStats && (<>
              <h3 className="text-white font-bold mb-3">Page Visitors</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[
                  { label: 'Today', value: visitorStats.today_visits, color: 'text-blue-400' },
                  { label: 'Yesterday', value: visitorStats.yesterday_visits, color: 'text-cyan-400' },
                  { label: 'This Week', value: visitorStats.week_visits, color: 'text-purple-400' },
                  { label: 'This Month', value: visitorStats.month_visits, color: 'text-orange-400' },
                ].map((s) => (
                  <div key={s.label} className="bg-white/5 rounded-xl p-4 border border-white/10 text-center">
                    <div className={`text-3xl font-extrabold ${s.color}`}>{s.value}</div>
                    <div className="text-white/40 text-xs font-bold uppercase tracking-wider mt-1">{s.label}</div>
                  </div>
                ))}
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <h3 className="text-white font-bold text-sm mb-3">Unique Visitors</h3>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: 'Today', value: visitorStats.unique_today, color: 'text-blue-400' },
                      { label: 'Week', value: visitorStats.unique_week, color: 'text-emerald-400' },
                      { label: 'Month', value: visitorStats.unique_month, color: 'text-purple-400' },
                    ].map((s) => (
                      <div key={s.label} className="text-center bg-white/5 rounded-lg p-3">
                        <div className={`text-xl font-extrabold ${s.color}`}>{s.value}</div>
                        <div className="text-white/30 text-[10px] font-bold uppercase">{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <h3 className="text-white font-bold text-sm mb-3">Last 7 Days</h3>
                  <div className="flex items-end justify-between h-24 gap-1">
                    {visitorStats.daily_visits.map((day, index) => {
                      const maxCount = Math.max(...visitorStats.daily_visits.map(d => d.count), 1);
                      const height = (day.count / maxCount) * 100;
                      return (
                        <div key={day.date} className="flex-1 flex flex-col items-center">
                          <div className="text-[10px] text-white/40 font-bold mb-1">{day.count}</div>
                          <div className="w-full bg-emerald-500/60 rounded-t" style={{ height: `${Math.max(height, 4)}%` }}></div>
                          <div className="text-[9px] text-white/30 mt-1">{new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-emerald-600 to-cyan-600 rounded-xl p-6 text-center">
                <div className="text-4xl font-extrabold text-white">{visitorStats.total_visits}</div>
                <div className="text-white/70 text-sm font-bold">Total Page Views</div>
              </div>
            </>)}
          </div>
        )}

        {/* ========== SETTINGS TAB ========== */}
        {activeTab === 'settings' && (
          <div>
            <h2 className="text-xl font-extrabold text-white mb-4">Settings</h2>
            <div className="bg-white/5 rounded-xl p-6 border border-white/10 space-y-5">
              <div>
                <label className="block text-xs font-bold text-white/60 mb-1 uppercase tracking-wider">Telegram Link</label>
                <input type="url" value={settings.telegram_link} onChange={(e) => setSettings({ ...settings, telegram_link: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:ring-2 focus:ring-emerald-500" placeholder="https://t.me/..." />
              </div>
              <div>
                <label className="block text-xs font-bold text-white/60 mb-1 uppercase tracking-wider">Subscription Price Text</label>
                <input type="text" value={settings.subscription_price} onChange={(e) => setSettings({ ...settings, subscription_price: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:ring-2 focus:ring-emerald-500" placeholder="$10/week" />
              </div>
              <div>
                <label className="block text-xs font-bold text-white/60 mb-1 uppercase tracking-wider">Free Tip Default Image URL</label>
                {settings.free_tip_image && <img src={settings.free_tip_image} alt="Preview" className="h-20 mb-2 rounded-lg object-cover" />}
                <input type="url" value={settings.free_tip_image || ''} onChange={(e) => setSettings({ ...settings, free_tip_image: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 text-sm" placeholder="https://i.imgur.com/your-mix-image.png" />
              </div>
              <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                <label className="block text-xs font-bold text-white/60 mb-1 uppercase tracking-wider">Email Notifications</label>
                <p className="text-white/40 text-xs">New tip posts automatically send emails to all users via SendGrid.</p>
              </div>
              <button onClick={handleSaveSettings} disabled={savingSettings}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-lg font-bold transition-all disabled:opacity-50">
                {savingSettings ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
